import type React from "react";
import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";
import { $getNodeByKey, DecoratorNode } from "lexical";
import Image from "next/image";
import CloseIcon from "../../../icons/iconClose.comp";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect, useState } from "react";
import { useAutoSaveDraft } from "../hooks/useSaveDraft";
import { useImageRehost } from "../hooks/useImageRehost";
import { useEditorMeta } from "../hooks/useMeta";

export interface ImagePayload {
  altText: string;
  src: string;
  width?: number;
  height?: number;
  key?: NodeKey;
  productId?: string;
  locale?: string;
}

const ALLOWED_IMAGE_DOMAINS = ["s3.ap-east-1.amazonaws.com", "s3.af-south-1.amazonaws.com"];

export type SerializedImageNode = Spread<
  {
    altText: string;
    src: string;
    width?: number;
    height?: number;
    productId?: string;
    locale?: string;
  },
  SerializedLexicalNode
>;

function convertImageElement(domNode: Node): null | DOMConversionOutput {
  if (domNode instanceof HTMLImageElement) {
    const { alt: altText, src, width, height } = domNode;
    const node = $createImageNode({ altText, src, width, height,});
    return { node };
  }
  return null;
}

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;
  __width?: number;
  __height?: number;
  __productId?: string;
  __locale?: string;



  static getType(): string {
    return "image";
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__altText, node.__width, node.__height, node.__key, node.__productId, node.__locale);
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { altText, src, width, height, productId, locale} = serializedNode;
    const node = $createImageNode({
      altText,
      src,
      width,
      height,
      productId,
      locale,
    });
    return node;
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      src: this.getSrc(),
      width: this.getWidth(),
      height: this.getHeight(),
      productId: this.getProductId(),
      locale: this.getLocale(),
      type: "image",
      version: 1,
    };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: Node) => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  constructor(src: string, altText: string, width?: number, height?: number, key?: NodeKey, productId?: string, locale?: string) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width;
    this.__height = height;
    this.__productId = productId;
    this.__locale = locale;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("img");
    element.setAttribute("src", this.__src);
    element.setAttribute("alt", this.__altText);
    if (this.__width) {
      element.setAttribute("width", String(this.__width));
    }
    if (this.__height) {
      element.setAttribute("height", String(this.__height));
    }
    return { element };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement("span");
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  updateDOM(): false {
    return false;
  }

  getSrc(): string {
    return this.__src;
  }

  setSrc(src: string): void {
    const writable = this.getWritable();
    writable.__src = src;
  }

  getAltText(): string {
    return this.__altText;
  }

  getWidth(): number | undefined {
    return this.__width;
  }

  getHeight(): number | undefined {
    return this.__height;
  }

  getProductId(): string | undefined {
    return this.__productId;
  }

  getLocale(): string| undefined {
    return this.__locale;
  }

  decorate(): JSX.Element {
    const style: React.CSSProperties = {};
    
    if (this.__width) style.width = this.__width;
    if (this.__height) style.height = this.__height;


    return (
      <ImageComponent
        src={this.__src}
        altText={this.__altText}
        width={this.__width}
        height={this.__height}
        nodeKey={this.__key}
        productId={this.__productId}
        locale={this.__locale}
      />
    );
  }
}

export function $createImageNode({ altText, src, width, height, key, productId, locale}: ImagePayload): ImageNode {
  return new ImageNode(src, altText, width, height, key, productId, locale);
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}

function ImageComponent({
  src,
  altText,
  width,
  height,
  nodeKey,
  // productId,
  // locale,
}: {
  src: string;
  altText: string;
  width?: number;
  height?: number;
  nodeKey: NodeKey;
  productId?: string;
  locale?: string;
}): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const { productId, locale } = useEditorMeta();
  // const queryClient = useQueryClient();

  console.log("productID", productId, "locale", locale)
  const scheduleSave = useAutoSaveDraft(editor, productId, locale);
  const rehostImage = useImageRehost(editor, scheduleSave);

  const [newSrc, setNewSrc] = useState<string>(src);
  const [pending, setPending] = useState(false);


  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (!pending) return;
      e.preventDefault();
      e.returnValue = "";
    }

    if (pending) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [pending]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setPending(true);

      const newUrl = await rehostImage(src, nodeKey);

      if (mounted) setPending(false);
    })();

    return () => { mounted = false };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, productId]);



  const handleDelete = () => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (node) {
        node.remove();
      }
    });
    scheduleSave();
  };


  return (
  <>
    {pending && (
      <div className="fixed top-4 right-4 z-[9999]">
        <div className="px-4 py-2 bg-black text-white rounded-lg shadow-md text-sm opacity-90">
          Uploading image… Don’t close the page.
        </div>
      </div>
    )}

    <div className="relative w-[250px] h-[250px] my-3 border-2">
      <CloseIcon
        className="w-4 h-4 -right-1 absolute -top-1 bg-red-400 text-white rounded-lg cursor-pointer z-20"
        onClick={handleDelete}
      />

      {pending ? (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" />
      ) : (
        <Image
          src={newSrc || "placeholder.svg"}
          alt={altText}
          fill
          unoptimized
          className="rounded-md"
        />
      )}
    </div>
  </>

  );
}

function isExternalUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.origin !== window.location.origin;
  } catch {
    return false;
  }
}

function isWhitelistedUrl(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    return ALLOWED_IMAGE_DOMAINS.includes(hostname);
  } catch {
    return false;
  }
}
